// Mobile-only transport surface: a Eurorack-style pedalboard pinned
// at the bottom of the viewport. Above-the-fold (TransportDeck)
// becomes pure identity — hamburger, brand dot, song title — and
// the muscle-memory transport actions live down here in the thumb
// zone. Mirrors the actual studio anatomy: patch panel up, foot
// controllers down.
//
// Switch layout:
//   [▶/❚❚/▸  PLAY ]   [■ STOP]   [● REC]   [◎ SAVE]   [⋯ MORE]
//   primary 2x flex   1x          1x         1x          1x
//
// PLAY morphs by phase: ▶ Play (idle/ready) → ❚❚ Pause (playing) →
// ▸ Resume (paused). Same position, same finger, different verb.
// STOP stays as a distinct switch because it does a different
// thing (halts pattern, doesn't keep it ready to resume).
//
// MORE opens a slide-up sheet for low-frequency actions: Restart
// from cycle 0, jump to Takes (when any have been captured), and
// any future expandable controls.
//
// Renders nothing on md+ — desktop keeps the existing TransportDeck
// transport toolbar.

import { useEffect, useState } from 'react';
import type { PlayerPhase } from '../strudel/types';
import type { RecorderState } from '../recording/recorder';

type Props = {
  phase: PlayerPhase;
  onPlay: () => void;
  onPauseToggle: () => void;
  onRestart: () => void;
  onStop: () => void;
  onSave: () => void;
  dirty: boolean;
  recorderState: RecorderState;
  recordDurationMs: number;
  onToggleRecord: () => void;
  recordingAvailable: boolean;
  takesCount: number;
  onOpenTakes: () => void;
};

export function MobilePedalboard({
  phase,
  onPlay,
  onPauseToggle,
  onRestart,
  onStop,
  onSave,
  dirty,
  recorderState,
  recordDurationMs,
  onToggleRecord,
  recordingAvailable,
  takesCount,
  onOpenTakes,
}: Props) {
  const [moreOpen, setMoreOpen] = useState(false);

  // Esc closes the More sheet.
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMoreOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const playing = phase === 'playing';
  const paused = phase === 'paused';
  const playable =
    phase === 'ready' || phase === 'playing' || phase === 'paused';
  const recording =
    recorderState === 'recording' || recorderState === 'paused';

  // Primary morph table — same position, different verb per phase.
  const primary = paused
    ? { glyph: '▸', label: 'Resume', handler: onPauseToggle }
    : playing
      ? { glyph: '❚❚', label: 'Pause', handler: onPauseToggle }
      : { glyph: '▶', label: 'Play', handler: onPlay };

  // REC label morphs to a live duration while recording so the
  // pedalboard doubles as a recording timer without needing extra
  // chrome. Format is m:ss for compactness.
  const recLabel = recording ? formatDuration(recordDurationMs) : 'Rec';

  return (
    <>
      {moreOpen && (
        <MoreSheet
          onClose={() => setMoreOpen(false)}
          onRestart={() => {
            onRestart();
            setMoreOpen(false);
          }}
          restartDisabled={!playable}
          takesCount={takesCount}
          onOpenTakes={() => {
            onOpenTakes();
            setMoreOpen(false);
          }}
          recording={recording}
          recordDurationMs={recordDurationMs}
        />
      )}

      <nav
        aria-label="Transport pedalboard"
        // md:hidden — desktop keeps the inline TransportDeck buttons.
        // safe-pb clears the iPhone home indicator; safe-pl/pr clear
        // landscape notches. The thicker top border + Eurorack screws
        // signal "this is a separate piece of gear bolted to the
        // bottom of the chassis."
        className="md:hidden relative flex items-stretch border-t-2 border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-950 safe-pb safe-pl safe-pr shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_-4px_12px_-6px_rgba(0,0,0,0.6)]"
      >
        {/* Pedalboard mounting screws — top corners only. The bottom
            of the unit sits flush with the screen edge. */}
        <span
          aria-hidden="true"
          className="absolute top-1.5 safe-screw-left w-1 h-1 rounded-full bg-zinc-800 shadow-[inset_0_0.5px_0.5px_rgba(255,255,255,0.15)]"
        />
        <span
          aria-hidden="true"
          className="absolute top-1.5 safe-screw-right w-1 h-1 rounded-full bg-zinc-800 shadow-[inset_0_0.5px_0.5px_rgba(255,255,255,0.15)]"
        />

        <FootSwitch
          variant="primary"
          glyph={primary.glyph}
          label={primary.label}
          onClick={primary.handler}
          disabled={!playable}
          active={playing}
          flex={2}
        />
        <FootSwitch
          variant="stop"
          glyph="■"
          label="Stop"
          onClick={onStop}
          disabled={!playing && !paused}
        />
        <FootSwitch
          variant="rec"
          glyph="●"
          label={recLabel}
          onClick={onToggleRecord}
          disabled={!recording && !recordingAvailable}
          active={recording}
        />
        <FootSwitch
          variant="save"
          glyph={dirty ? '◎' : '✓'}
          label={dirty ? 'Save' : 'Saved'}
          onClick={onSave}
          dimWhenInactive={!dirty}
        />
        <FootSwitch
          variant="more"
          glyph="⋯"
          label="More"
          onClick={() => setMoreOpen(true)}
          active={moreOpen}
        />
      </nav>
    </>
  );
}

// ─── FootSwitch ─────────────────────────────────────────────────────

type SwitchVariant = 'primary' | 'stop' | 'rec' | 'save' | 'more';

function FootSwitch({
  variant,
  glyph,
  label,
  onClick,
  disabled,
  active,
  dimWhenInactive,
  flex = 1,
}: {
  variant: SwitchVariant;
  glyph: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  dimWhenInactive?: boolean;
  flex?: number;
}) {
  const palette = paletteFor(variant, active, dimWhenInactive);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{ flex }}
      aria-label={label}
      aria-pressed={active}
      // Press translates the entire button down by 1.5px and shifts
      // the inset shadow — reads as a stomped pedal switch. The
      // border-r between switches is the only visual seam.
      className={
        'foot-switch relative flex flex-col items-center justify-center gap-0.5 ' +
        'min-h-[60px] py-2 px-1 ' +
        'border-r border-zinc-900 last:border-r-0 ' +
        'transition-[transform,filter,background-color] duration-75 ' +
        'disabled:opacity-30 disabled:cursor-not-allowed ' +
        'active:translate-y-[1.5px] cursor-pointer ' +
        palette
      }
    >
      {/* Phosphor LED — sits above the glyph when the switch is in
          its "active/lit" state. Subtle bloom via box-shadow. */}
      {active && variant !== 'more' && (
        <span
          aria-hidden="true"
          className={
            'absolute top-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full ' +
            ledForVariant(variant)
          }
        />
      )}
      <span
        aria-hidden="true"
        className="text-[20px] leading-none font-display tabular-nums"
      >
        {glyph}
      </span>
      <span className="text-[8px] tracking-[0.2em] uppercase font-display leading-none mt-0.5 tabular-nums">
        {label}
      </span>
    </button>
  );
}

function paletteFor(
  variant: SwitchVariant,
  active: boolean | undefined,
  dimWhenInactive: boolean | undefined,
): string {
  switch (variant) {
    case 'primary':
      return active
        ? 'bg-emerald-300 text-zinc-950 hover:brightness-95'
        : 'bg-yellow-300 text-zinc-950 hover:brightness-95';
    case 'stop':
      return 'bg-zinc-900 text-rose-300 hover:bg-zinc-800/80 hover:text-rose-200';
    case 'rec':
      return active
        ? 'bg-red-500 text-white rec-pulse hover:brightness-95'
        : 'bg-zinc-900 text-red-400 hover:bg-zinc-800/80 hover:text-red-300';
    case 'save':
      return dimWhenInactive
        ? 'bg-zinc-900 text-zinc-500 hover:bg-zinc-800/80 hover:text-zinc-300'
        : 'bg-zinc-900 text-yellow-300 hover:bg-zinc-800/80 hover:text-yellow-200';
    case 'more':
      return active
        ? 'bg-zinc-800 text-yellow-300'
        : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800/80 hover:text-zinc-200';
  }
}

function ledForVariant(variant: SwitchVariant): string {
  switch (variant) {
    case 'primary':
      return 'bg-emerald-200 shadow-[0_0_8px_rgba(110,231,183,0.95)]';
    case 'rec':
      return 'bg-red-200 shadow-[0_0_8px_rgba(254,202,202,0.9)]';
    case 'save':
      return 'bg-yellow-200 shadow-[0_0_8px_rgba(253,224,71,0.9)]';
    default:
      return 'bg-yellow-300 shadow-[0_0_6px_rgba(253,224,71,0.7)]';
  }
}

// ─── More sheet ─────────────────────────────────────────────────────

function MoreSheet({
  onClose,
  onRestart,
  restartDisabled,
  takesCount,
  onOpenTakes,
  recording,
  recordDurationMs,
}: {
  onClose: () => void;
  onRestart: () => void;
  restartDisabled: boolean;
  takesCount: number;
  onOpenTakes: () => void;
  recording: boolean;
  recordDurationMs: number;
}) {
  return (
    <>
      {/* Scrim — tapping outside closes. */}
      <div
        className="md:hidden fixed inset-0 z-[55] bg-zinc-950/70 backdrop-blur-sm pedalboard-scrim-in"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Slide-up sheet anchored to the bottom edge so it visually
          extends out of the pedalboard itself, not floating over it. */}
      <div
        role="dialog"
        aria-label="More transport actions"
        className="md:hidden fixed left-0 right-0 bottom-0 z-[60] border-t-2 border-yellow-300/40 bg-gradient-to-b from-zinc-900 to-zinc-950 safe-pb safe-pl safe-pr shadow-[0_-12px_32px_-8px_rgba(0,0,0,0.65),0_0_24px_-6px_rgba(253,224,71,0.18)] pedalboard-sheet-in"
      >
        <div className="px-4 pt-3 pb-3">
          <div className="flex items-center justify-between mb-3">
            <span className="flex items-center gap-2 font-display text-[10px] tracking-[0.32em] uppercase text-yellow-300">
              <span
                aria-hidden="true"
                className="h-1 w-1 rounded-full bg-yellow-300 shadow-[0_0_6px_rgba(253,224,71,0.7)]"
              />
              <span>Transport · More</span>
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-zinc-500 hover:text-yellow-300 w-7 h-7 grid place-items-center rounded-sm border border-zinc-800 hover:border-yellow-300/40 transition-colors"
            >
              <svg
                viewBox="0 0 16 16"
                className="w-3 h-3"
                stroke="currentColor"
                strokeWidth={1.4}
                fill="none"
              >
                <path d="M3 3 L13 13 M13 3 L3 13" />
              </svg>
            </button>
          </div>

          {recording && (
            <div className="mb-2 px-3 py-2 border border-red-500/40 bg-red-500/10 text-red-200 text-[11px] font-display tracking-[0.18em] uppercase rounded-sm flex items-center gap-2">
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full bg-red-400 rec-pulse"
              />
              <span>Recording · {formatDuration(recordDurationMs)}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <SheetAction
              glyph="↺"
              label="Restart"
              hint="From cycle 0"
              onClick={onRestart}
              disabled={restartDisabled}
            />
            {takesCount > 0 ? (
              <SheetAction
                glyph="▸"
                label={`Takes (${takesCount})`}
                hint="Open recordings"
                onClick={onOpenTakes}
              />
            ) : (
              <div
                aria-hidden="true"
                className="px-3 py-2.5 border border-dashed border-zinc-800 rounded-sm text-zinc-700 text-[10px] font-display tracking-[0.18em] uppercase flex items-center"
              >
                No takes yet
              </div>
            )}
          </div>

          {/* Tiny pedalboard cable — playful detail that connects the
              sheet to the rest of the chassis. Pure decoration. */}
          <div
            aria-hidden="true"
            className="mt-3 flex items-center gap-1 text-[8px] tracking-[0.32em] uppercase font-display text-zinc-700"
          >
            <span className="h-px flex-1 bg-zinc-800" />
            <span>tap outside to close</span>
            <span className="h-px flex-1 bg-zinc-800" />
          </div>
        </div>
      </div>
    </>
  );
}

function SheetAction({
  glyph,
  label,
  hint,
  onClick,
  disabled,
}: {
  glyph: string;
  label: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        'flex items-center gap-3 px-3 py-2.5 rounded-sm border border-zinc-800 ' +
        'bg-zinc-900/60 text-left transition-colors ' +
        'disabled:opacity-30 disabled:cursor-not-allowed ' +
        'hover:border-yellow-300/40 hover:bg-zinc-900/80 active:translate-y-[1px]'
      }
    >
      <span
        aria-hidden="true"
        className="text-[18px] leading-none text-yellow-300/90"
      >
        {glyph}
      </span>
      <span className="flex flex-col min-w-0">
        <span className="font-display text-[11px] tracking-[0.18em] uppercase text-zinc-100 leading-none">
          {label}
        </span>
        <span className="font-display text-[9px] tracking-[0.18em] uppercase text-zinc-500 mt-1 leading-none truncate">
          {hint}
        </span>
      </span>
    </button>
  );
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
